import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import teacherSubjectService from "../../services/teacherSubjectService";
import { queryKeys } from "./queryKeys";

// ==================== QUERY HOOKS ====================

export const useTeacherSubjects = (filters = {}) => {
  return useQuery({
    queryKey: [...queryKeys.teacherSubject.all, filters],
    queryFn: () => teacherSubjectService.getTeacherSubjects(filters),
    staleTime: 5 * 60 * 1000,
  });
};

export const useTeacherSubjectById = (id) => {
  return useQuery({
    queryKey: queryKeys.teacherSubject.detail(id),
    queryFn: () => teacherSubjectService.getTeacherSubjectById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSubjectsByTeacher = (teacherId) => {
  return useQuery({
    queryKey: queryKeys.teacherSubject.byTeacher(teacherId),
    queryFn: () => teacherSubjectService.getSubjectsByTeacher(teacherId),
    enabled: !!teacherId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useTeachersBySubject = (subjectId, classId = null) => {
  return useQuery({
    queryKey: queryKeys.teacherSubject.bySubject(subjectId, classId),
    queryFn: () => teacherSubjectService.getTeachersBySubject(subjectId, classId),
    enabled: !!subjectId && !!classId,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });
};

export const useTeacherAvailability = (teacherId) => {
  return useQuery({
    queryKey: queryKeys.teacherSubject.availability(teacherId),
    queryFn: () => teacherSubjectService.getTeacherAvailability(teacherId),
    enabled: !!teacherId,
    staleTime: 5 * 60 * 1000,
  });
};

// ==================== MUTATION HOOKS ====================

export const useAssignTeacherToSubject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => teacherSubjectService.assignTeacherToSubject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.teacherSubject.all,
      });
    },
  });
};

export const useUpdateTeacherSubject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => teacherSubjectService.updateTeacherSubject(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.teacherSubject.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.teacherSubject.detail(variables.id),
      });
    },
  });
};

export const useDeleteTeacherSubject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => teacherSubjectService.deleteTeacherSubject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.teacherSubject.all,
      });
    },
  });
};

export const useSetTeacherAvailability = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teacherId, data }) => 
      teacherSubjectService.setTeacherAvailability(teacherId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.teacherSubject.availability(variables.teacherId),
      });
    },
  });
};
